<template>
  <div class="w-full flex">
    <div class="block w-1/4 bg-gray-800 h-500 overflow-y-auto">
      <nav class="border-b bg-white border-gray-200" aria-label="Navigation">
        <ol role="list"
            class="max-w-screen-xl w-full mx-auto px-1 flex space-x-2 items-center justify-center py-4">
          <li class="flex items-center">
            <a @click.prevent="step = 'template'"
               class="xl:ml-4 text-sm cursor-pointer"
               :class="step === 'template' ? 'hover:text-gray-700 text-wiq-green font-extrabold' : 'font-medium text-gray-500 hover:text-wiq-green'"
               :aria-current="step === 'template' ? 'page' : false">
              Template
            </a>
          </li>
          <li class="flex">
            <div class="flex items-center">
              <svg class="flex-shrink-0 w-6 h-full text-gray-200" viewBox="0 0 24 44" preserveAspectRatio="none"
                   fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z"/>
              </svg>
              <a @click.prevent="step = 'dimensions'"
                 class="xl:ml-4 text-sm cursor-pointer"
                 :class="step === 'dimensions' ? 'hover:text-gray-700 text-wiq-green font-extrabold' : 'font-medium text-gray-500 hover:text-wiq-green'"
                 :aria-current="step === 'dimensions' ? 'page' : false">
                Dimensions
              </a>
            </div>
          </li>
          <li class="flex">
            <div class="flex items-center">
              <svg class="flex-shrink-0 w-6 h-full text-gray-200" viewBox="0 0 24 44" preserveAspectRatio="none"
                   fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z"/>
              </svg>
              <a @click.prevent="step = 'details'"
                 class="xl:ml-4 text-sm cursor-pointer"
                 :class="step === 'details' ? 'hover:text-gray-700 text-wiq-green font-extrabold' : 'font-medium text-gray-500 hover:text-wiq-green'"
                 :aria-current="step === 'details' ? 'page' : false">
                Customize
              </a>
            </div>
          </li>
        </ol>
      </nav>
      <div class="w-full flex-col px-8 pt-12 pb-8">
        <transition
            enter-active-class="duration-150 ease-out"
            enter-class="opacity-0 translate-x-6"
            enter-to-class="opacity-100"
            leave-active-class="duration-100 ease-in"
            leave-class="opacity-100"
            leave-to-class="opacity-0 -translate-x-6"
            mode="out-in"
        >
          <div v-show="step === 'template'">
            <h1 class="text-gray-50 text-2xl font-extrabold">Pick a template</h1>
            <ul class="divide-y divide-gray-200 mt-12">
              <li @click="selectTemplate(template)" v-for="(template, index) in templates"
                  class="flex flex-col space-y-4 cursor-pointer" :class="index === 0 ? 'pb-8': 'pb-8 pt-8'">
                <h2 class="flex items-center space-x-4 text-gray-100 text-xl font-bold justify-between">
              <span>
                {{ template.name }}
              </span>
                  <svg v-if="template.selected" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-wiq-green"
                       fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </h2>
                <img v-if="template.previewImg" :src="template.previewImg" class="w-full h-auto bg-white rounded-sm p-4">
                <div v-else class="bg-gray-200 w-full aspect-h-3 aspect-w-4 rounded-sm"></div>
              </li>
              <li class="flex flex-col pb-8 pt-8">
                <a href="mailto:support@wrestlingiq.com?subject=Promo template idea"
                   class="cursor-pointer w-full bg-wiq-green text-white text-xl font-bold rounded-full text-center px-4 py-4">
                  Suggest a template
                </a>
                <p class="flex text-gray-200 text-sm font-normal leading-tight text-center mt-2 mx-auto">
                  If you have an idea for a new template—or have seen a flyer design you really like—send it over!
                </p>
              </li>
            </ul>
          </div>
        </transition>
        <transition
            enter-active-class="duration-150 ease-out"
            enter-class="opacity-0 translate-x-6"
            enter-to-class="opacity-100"
            leave-active-class="duration-100 ease-in"
            leave-class="opacity-100"
            leave-to-class="opacity-0 -translate-x-6"
            mode="out-in"
        >
          <div v-show="step === 'dimensions'">
            <template v-if="selectedTemplate === undefined">
              <h1 class="text-gray-50 text-2xl font-extrabold">Pick a template first</h1>
            </template>
            <template v-else>
              <p class="text-sm text-base font-semibold uppercase text-wiq-green tracking-wider">
                {{ selectedTemplate.name }}</p>
              <h1 class="text-gray-50 text-2xl font-extrabold">Pick a size</h1>
              <ul class="divide-y divide-gray-200 mt-12">
                <li @click="selectDimension(dimension)" v-for="(dimension, index) in availableDimensions"
                    class="flex flex-col cursor-pointer" :class="index === 0 ? 'pb-8': 'pb-8 pt-8'">
                  <h2 class="flex items-center space-x-4 text-gray-100 text-xl font-bold justify-between">
                    <span>
                      {{ dimension.title }}
                    </span>
                    <svg v-if="dimension.selected" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-wiq-green"
                         fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </h2>
                  <p class="text-gray-200">
                    {{ dimension.dimensions }}
                  </p>
                </li>
              </ul>
            </template>
          </div>
        </transition>
        <transition
            enter-active-class="duration-150 ease-out"
            enter-class="opacity-0 translate-x-6"
            enter-to-class="opacity-100"
            leave-active-class="duration-100 ease-in"
            leave-class="opacity-100"
            leave-to-class="opacity-0 -translate-x-6"
            mode="out-in"
        >
          <div v-show="step === 'details'">
            <template v-if="selectedDimension === undefined || selectedTemplate === undefined">
              <h1 class="text-gray-50 text-2xl font-extrabold">Pick a template and dimension first</h1>
            </template>
            <template v-else>
              <p class="text-sm text-base font-semibold uppercase text-wiq-green tracking-wider">
                {{ selectedTemplate.name }}</p>
              <h1 class="text-gray-50 text-2xl font-extrabold">Customize</h1>
              <div class="mt-6 flex flex-col gap-y-6">
                <div v-for="field in $options.p5Wrapper.userEditableFields()">
                  <label :for="`field-${field.id}`" class="block text-sm font-medium text-gray-200">
                    {{ field.prompt }}
                  </label>
                  <div class="mt-1" v-if="field.type === 'text'">
                    <input type="text" :name="`field-${field.id}`" :id="`field-${field.id}`"
                           :placeholder="field.placeholder"
                           class="shadow-sm focus:ring-wiq-green focus:border-wiq-green block w-full sm:text-sm border-gray-200 rounded-md"
                           v-on:input="updateProperty(selectedDimension, field.property, $event.target.value)"
                    >
                  </div>
                  <div class="mt-1" v-if="field.type === 'multiLineText'">
                    <textarea :name="`field-${field.id}`" :id="`field-${field.id}`"
                           :placeholder="field.placeholder"
                           class="shadow-sm focus:ring-wiq-green focus:border-wiq-green block w-full sm:text-sm border-gray-200 rounded-md"
                           v-on:input="updateProperty(selectedDimension, field.property, $event.target.value)"
                    >
                    </textarea>
                  </div>
                  <div v-else-if="field.type === 'photo'" class="mt-1 text-gray-200 text-sm">
                    <pintura-input-wrapper @photo-added="changeImg" :photo-id="field.id" :pintura-opts="field.pinturaOpts"></pintura-input-wrapper>
                  </div>
                  <div v-else-if="field.type === 'color'">
                    <input type="color" :name="`field-${field.id}`" :id="`field-${field.id}`"
                           class="mt-1"
                           :value="$options.p5Wrapper[field.property]"
                           v-on:input="updateProperty(selectedDimension, field.property, $event.target.value)"
                    >
                  </div>
                </div>
              </div>
              <button
                  class="mt-6 inline-flex items-center justify-center w-full shadow px-4 py-2 border border-gray-50 text-base leading-6 font-semibold rounded-md text-white bg-wiq-green hover:text-gray-50 focus:outline-none focus:border-blue-200 focus:ring-blue-200 active:bg-gray-50 active:text-gray-50 transition duration-150 ease-in-out"
                  @click="exportFile">
                Download
                <svg xmlns="http://www.w3.org/2000/svg" class="ml-2 -mr-0.5 h-4 w-4" viewBox="0 0 20 20"
                     fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd"
                        d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                        clip-rule="evenodd"/>
                </svg>
              </button>
            </template>
          </div>
        </transition>
      </div>
    </div>
    <div class="w-3/4 bg-white h-screen sticky top-0">
      <div class="max-w-5xl mx-auto py-4">
        <div class="flex justify-between items-center">
          <div>
            <a href="/?ref=promo-generator-image">
              <p class="text-center text-base font-semibold uppercase text-wiq-green tracking-wider">
                FREE Promo Generator
              </p>
              <img src="https://mktg-cdn.wrestlingiq.com/logos/wiq/text@2x.png" class="h-8 w-auto sm:h-12"
                   alt="WrestlingIQ logo"/>
            </a>
          </div>
          <div class="relative">
            <a href="/?ref=promo-generator-textlink" class="flex items-center text-gray-800 cursor-pointer space-x-1 hover:text-wiq-green text-base underline"
               target="_blank">
              <span>
                Learn more about <span class="text-wiq-green font-semibold">WrestlingIQ</span>
              </span>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                   stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round"
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
              </svg>
            </a>
            <svg width="59" height="22" viewBox="0 0 59 22" fill="currentColor" xmlns="http://www.w3.org/2000/svg"
                 class="absolute -top-6 left-24 transform lg:block rotate-6 stroke-2 text-wiq-green">
              <path
                  d="M53.0252 6.243C52.3687 4.4345 51.8317 2.28949 51.8317 1.47749C51.8317 0.497493 52.0842 0 52.5817 0C53.0452 0 53.3317 0.470006 53.3317 1.23151C53.3317 2.77901 55.0608 7.2145 56.3933 9.086C56.9483 9.866 57.5963 11.179 57.8333 12.0045C58.1928 13.259 58.0847 13.6335 57.1727 14.288C56.2287 14.966 50.5552 18.712 47.0187 20.9925C46.0657 21.6075 45.7563 21.6215 45.2243 21.0755C44.6978 20.5345 44.9427 20.0995 46.5817 18.663C47.6817 17.699 50.0987 16.0055 51.9527 14.9005C53.8067 13.7955 55.2127 12.7115 55.0767 12.4925C54.5932 11.709 50.0738 10.489 45.6148 9.9375C35.9438 8.741 21.9292 10.2015 13.2098 13.3145C7.77123 15.256 1.72672 18.5055 2.03922 19.3195C2.34372 20.1125 0.564236 20.2805 0.0842361 19.504C-0.860264 17.976 6.27525 13.817 14.5918 11.0485C23.1278 8.20749 29.9862 7.30451 40.3317 7.66051C47.8532 7.91951 49.9902 8.17451 53.1502 9.18851L54.2192 9.53149L53.0252 6.243Z"></path>
            </svg>
          </div>
        </div>
        <div class="grid place-items-center mt-24">
          <div id="p5canvas" ref="p5canvas"></div>
        </div>
        <div class="absolute max-w-5xl w-full bottom-5 text-center text-xs">
          <p class="text-gray-500">
            This tool works best in recent versions of Chrome, Firefox or Safari.<br>
            Having issues? <a target="_blank" class="underline" href="/contact">Contact WrestlingIQ</a>.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { hallOfFamePreviewImg, hallOfFameDimensions } from './templates/hall-of-fame.js';
import { simpleBorderPreviewImg, simpleBorderDimensions } from './templates/event-simple-border.js';
import PinturaInputWrapper from './pintura-input-wrapper.vue';

export default {
  name: 'promo-generator',
  components: { PinturaInputWrapper },
  props: {},
  // This is a custom, non-reactive reference to the p5 wrapper so Vue doesn't fuck up the global scope on P5
  // Access like this.$options.p5Wrapper
  p5Wrapper: null,
  data() {
    return {
      // Navigation steps template -> dimensions -> values
      step: 'template',
      templates: [
        {
          id: 1,
          name: 'Event Promo (simple border)',
          dimensions: simpleBorderDimensions(),
          selected: false,
          previewImg: simpleBorderPreviewImg(),
        },
        {
          id: 2,
          name: 'Hall of Fame',
          dimensions: hallOfFameDimensions(),
          selected: false,
          previewImg: hallOfFamePreviewImg(),
        },
      ]
    };
  },
  computed: {
    selectedTemplate() {
      return _.find(this.templates, ['selected', true]);
    },
    selectedDimension() {
      if (this.selectedTemplate === undefined) {
        return undefined;
      }

      let selectedDimension = undefined;
      this.selectedTemplate.dimensions.forEach((dimension) => {
        if (dimension.selected) {
          selectedDimension = dimension;
        }
      });

      return selectedDimension;
    },
    availableDimensions() {
      let template = this.selectedTemplate;
      return template?.dimensions ?? [];
    }
  },
  methods: {
    selectDimension(dimension) {
      this.templates.forEach((template) => {
        template.dimensions.forEach((dimension) => {
          dimension.selected = false;
        });
      });
      dimension.selected = true;

      // Cleanup the previous instances if needed.
      // Each template should implement a cleanup method so they can do things
      // like release the membery of the off screen graphics buffer before we redo things.
      if (this.$options.p5Wrapper !== null) {
        this.$options.p5Wrapper.canvasWillBeRemoved();
        // Explicitly remove the on screen canvas and elements
        this.$options.p5Wrapper.remove();
        // Explitily set it to null to get garbage collected
        this.$options.p5Wrapper = null;
      }

      // Invoke the P5 wrapper for drawing this template
      this.$options.p5Wrapper = dimension.initializer(this.$refs.p5canvas);
      this.step = 'details';
    },
    selectTemplate(template) {
      this.templates.forEach((template) => {
        template.selected = false;
      });
      template.selected = true;
      this.step = 'dimensions';
    },
    updateProperty(dimension, propName, value) {
      //dimension[propName] = value;
      this.$options.p5Wrapper[propName] = value;
      this.$options.p5Wrapper.redraw();
    },
    changeImg(file, photoId) {
      this.fileToBitmap(file, photoId);
    },
    async fileToBitmap(file, photoId) {
      let field = _.find(this.$options.p5Wrapper.userEditableFields(), ['id', photoId]);
      // Polyfill needed for createimagebitmap? Maybe https://observablehq.com/@ehouais/how-to-get-image-data-from-an-url-or-a-file
      this.$options.p5Wrapper[field.property] = await createImageBitmap(file);
      this.$options.p5Wrapper.redraw();
    },
    exportFile() {
      this.$options.p5Wrapper.exportGraphicBuffer();
      if (window.fathom !== undefined) {
        window.fathom.trackGoal('JZF84WTO', 1);
      }
    }
  }
};
</script>
